'use client'

import React from 'react'
import styles from './feedback.module.css'
import { EnterpriseSpinner } from './EnterpriseSpinner'

export type FeedbackStateVariant = 'empty' | 'error' | 'loading'

export interface FeedbackStateAction {
  label: string
  onClick: () => void
  /** primary = filled brand; secondary = outline */
  tone?: 'primary' | 'secondary'
  icon?: React.ReactNode
  disabled?: boolean
}

export interface FeedbackStateProps {
  variant?: FeedbackStateVariant
  title: string
  description?: string
  /** Icon node; ignored for loading (spinner used) */
  icon?: React.ReactNode
  /** Primary / secondary next-step actions */
  actions?: FeedbackStateAction[]
  compact?: boolean
  className?: string
  /** Accessible label override */
  'aria-label'?: string
}

/**
 * Calm empty / error / loading surface with a clear next action.
 * Shared by tables and domain panels (ui-ux-pro-max).
 */
export function FeedbackState({
  variant = 'empty',
  title,
  description,
  icon,
  actions = [],
  compact = false,
  className = '',
  'aria-label': ariaLabel,
}: FeedbackStateProps) {
  const role = variant === 'error' ? 'alert' : variant === 'loading' ? 'status' : 'status'
  const iconClass =
    variant === 'error'
      ? `${styles.stateIcon} ${styles.stateIconError}`
      : `${styles.stateIcon} ${styles.stateIconMuted}`

  return (
    <div
      className={`${styles.state} ${compact ? styles.stateCompact : ''} ${className}`.trim()}
      role={role}
      aria-live={variant === 'loading' ? 'polite' : undefined}
      aria-busy={variant === 'loading' ? true : undefined}
      aria-label={ariaLabel || title}
    >
      {variant === 'loading' ? (
        <div className={styles.stateIcon} aria-hidden="true">
          <EnterpriseSpinner size="lg" variant="primary" aria-label={title} />
        </div>
      ) : icon ? (
        <div className={iconClass} aria-hidden="true">
          {icon}
        </div>
      ) : null}

      <p className={styles.stateTitle}>{title}</p>

      {description ? <p className={styles.stateDesc}>{description}</p> : null}

      {actions.length > 0 ? (
        <div className={styles.stateActions}>
          {actions.map((action) => {
            const tone = action.tone || (variant === 'error' ? 'primary' : 'secondary')
            const btnClass =
              tone === 'primary'
                ? `${styles.stateAction} ${styles.stateActionPrimary}`
                : `${styles.stateAction} ${styles.stateActionSecondary}`
            return (
              <button
                key={action.label}
                type="button"
                className={btnClass}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.icon}
                {action.label}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

export default FeedbackState
